import { applySaleRatio, tiersForVariant } from "@/lib/storefront/products/tiers";
import type { PriceTierDTO } from "@/lib/storefront/products/service";
import type { CartAddon } from "./addons";
import { addonsUnitTotal } from "./addons";

type Resolvable = {
  price?: number;
  quantity: number;
  variant_id?: string | null;
  has_price_tiers?: boolean;
  price_tiers?: PriceTierDTO[] | null;
  /**
   * Active campaign discount expressed as sale/original, captured at hydration
   * where both numbers are known. `price` already has the sale applied, so this
   * exists purely to re-apply it on top of a tier price.
   */
  sale_ratio?: number | null;
  /** The viewer is in a price group, so a tier may only lower `price`. */
  in_price_group?: boolean;
  addons?: CartAddon[] | null;
};

/**
 * Returns the unit price the cart should display for an item at its current
 * quantity. Mirrors the order placed server-side: when the product has tier
 * pricing and a tier matches the quantity (with variant inheritance), the tier
 * sets the unit price; otherwise fall back to the snapshot price already on the
 * item (which already accounts for base/group/sale).
 *
 * Tier and campaign COMPOUND. The snapshot carries the sale, so a matched tier
 * would otherwise discard it — a bulk buyer silently losing the discount. When
 * a campaign is running we re-apply its ratio on top of the tier price.
 */
/**
 * The unit price a line costs, add-ons included.
 *
 * Tier and campaign discounts apply to the product portion only — an add-on is
 * priced as itself, which is both the conventional behaviour and the one that
 * keeps the arithmetic auditable against `order_items.addons`. Lines without
 * add-ons are unchanged, since `addonsUnitTotal` is 0 for them.
 */
export function resolveCartItemPrice(item: Resolvable): number {
  return resolveProductUnitPrice(item) + addonsUnitTotal(item.addons);
}

/**
 * What a discount preview asks about. Discounts come off the product's price,
 * never its add-ons, so this is the number checkout discounts.
 */
export function discountLines<T extends Resolvable & { product_id: string }>(
  items: T[],
) {
  return items.map((item) => ({
    product_id: item.product_id,
    quantity: item.quantity,
    unit_price: resolveProductUnitPrice(item),
  }));
}

/** The product's own unit price: base/group/sale, with a matched tier on top. */
export function resolveProductUnitPrice(item: Resolvable): number {
  const base = item.price ?? 0;
  const tiers = item.price_tiers;
  if (!item.has_price_tiers || !tiers || tiers.length === 0) return base;

  const applicable = tiersForVariant(tiers, item.variant_id);
  const matched = applicable
    .filter(
      (t) =>
        item.quantity >= t.min_qty &&
        (t.max_qty === null || item.quantity <= t.max_qty),
    )
    .sort((a, b) => b.min_qty - a.min_qty)[0];

  if (!matched) return base;
  // A tier only ever lowers a group price — a wholesale shopper's group price
  // can already beat the default tiers. Same rule as `composeUnitPrice`. A line
  // with no usable price (0/missing) keeps the tier, as it always has.
  const tiered = applySaleRatio(matched.unit_price, item.sale_ratio);
  return item.in_price_group && base > 0 ? Math.min(base, tiered) : tiered;
}

